import {
    DocumentViewer,
    formatDate,
    ProviderDocumentUrl,
    ProviderLetter,
    renderDate,
    Spinner,
    SupportLetterAddress,
    SupportLetterTrackingEvent,
} from '@enter/core';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { RootStore } from 'stores';
import * as S from './claim-timeline-table.styles';

interface ILetterDetailProps {
    letter: ProviderLetter;
}

export const LetterDetail: React.FunctionComponent<ILetterDetailProps> = inject('rootStore')(
    observer(
        (props: ILetterDetailProps): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const { claimStore } = rootStore;
            const { letter } = props;
            const [loading, setLoading] = useState(false);
            const [documentUrl, setDocumentUrl] = useState<ProviderDocumentUrl | undefined>();

            useEffect(() => {
                (async (): Promise<void> => {
                    if (letter?.id && letter.document?.id) {
                        setLoading(true);
                        const url = await claimStore.viewLetterById(letter.id);
                        setDocumentUrl(url);
                        setLoading(false);
                    }
                })();
            }, [letter, claimStore]);

            const response = letter.response || {};
            const { to, expectedDeliveryDate, carrier, trackingNumber, trackingEvents } = response;

            const showAddress = (address?: SupportLetterAddress): string | undefined => {
                if (address) {
                    return _.join(
                        _.compact([
                            address.description,
                            address.company,
                            address.addressLineOne,
                            address.addressLineTwo,
                            `${address.city}, ${address.state} ${address.zip}`,
                            address.country,
                        ]),
                        '  \n', // NB: Markdown allows line returns if line ends with 2+ spaces
                    );
                }
            };

            const renderTrackingEvents = (trackingEvent: SupportLetterTrackingEvent, i: number): React.ReactElement => {
                return (
                    <S.ExpandTR key={`tracking-event-${i}`}>
                        {!!trackingEvent.time && (
                            <S.ExpandTD>{formatDate(trackingEvent.time, 'MMM dd, yyyy hh:mm')}</S.ExpandTD>
                        )}
                        <S.ExpandTD>{trackingEvent.name}</S.ExpandTD>
                        <S.ExpandTD>{trackingEvent.location}</S.ExpandTD>
                    </S.ExpandTR>
                );
            };

            return (
                <S.ExpandWrap key={letter.id}>
                    {loading ? (
                        <Spinner />
                    ) : (
                        <S.ExpandCol>
                            {documentUrl?.url && <DocumentViewer url={documentUrl.url} />}
                            <S.ExpandTable expand={true}>
                                <S.ExpandTBody>
                                    {!loading && (
                                        <>
                                            <S.ExpandTR>
                                                <S.ExpandTH>Carrier</S.ExpandTH>
                                                <S.ExpandTD>{carrier}</S.ExpandTD>
                                            </S.ExpandTR>
                                            <S.ExpandTR>
                                                <S.ExpandTH>Expected Delivery Date</S.ExpandTH>
                                                <S.ExpandTD>{renderDate(expectedDeliveryDate)}</S.ExpandTD>
                                            </S.ExpandTR>
                                            <S.ExpandTR>
                                                <S.ExpandTH>To</S.ExpandTH>
                                                <S.ExpandTD>
                                                    <S.MarkdownBlock>{showAddress(to)}</S.MarkdownBlock>
                                                </S.ExpandTD>
                                            </S.ExpandTR>
                                            <>
                                                {trackingNumber && (
                                                    <S.ExpandTR>
                                                        <S.ExpandTH>Tracking Number</S.ExpandTH>
                                                        <S.ExpandTD>{trackingNumber}</S.ExpandTD>
                                                    </S.ExpandTR>
                                                )}
                                                {trackingEvents && (
                                                    <>
                                                        <S.ExpandTR>
                                                            <S.ExpandTH>Tracking Events</S.ExpandTH>
                                                        </S.ExpandTR>
                                                        <S.ExpandTR>
                                                            <S.ExpandTD colSpan={2}>
                                                                <S.ExpandTable>
                                                                    <S.ExpandTBody>
                                                                        {_.map(trackingEvents, (trackingEvent, n) =>
                                                                            renderTrackingEvents(trackingEvent, n),
                                                                        )}
                                                                    </S.ExpandTBody>
                                                                </S.ExpandTable>
                                                            </S.ExpandTD>
                                                        </S.ExpandTR>
                                                    </>
                                                )}
                                            </>
                                        </>
                                    )}
                                </S.ExpandTBody>
                            </S.ExpandTable>
                        </S.ExpandCol>
                    )}
                </S.ExpandWrap>
            );
        },
    ),
);
